/**
 * Brazilian Portuguese catalogue.
 *
 * The `Record<MessageKey, string>` annotation is deliberate: it makes an
 * untranslated key a compile error rather than a silent English fallback at
 * runtime. Keys stay in the same order as `en.ts` so the two files diff well.
 *
 * Pseudocode keywords (`if`, `while`, `function`, …) are quoted verbatim in
 * these messages — the language itself is not translated in this version.
 */
import type { MessageKey } from '../types';

export const pt: Record<MessageKey, string> = {
  /* ---------------------------------------------------------------- *
   * Application shell
   * ---------------------------------------------------------------- */
  'app.name': '{product}',
  'app.tagline': 'Escreva a lógica. Veja o fluxograma.',
  'app.description':
    'Escreva pseudocódigo à esquerda e veja o fluxograma se montar à direita. Tudo roda no seu navegador.',
  'app.documentTitle': '{name} — {product}',
  'app.welcomeTitle': 'Boas-vindas ao {product}',
  'app.welcomeBody':
    'Descreva sua lógica em passos simples e o diagrama acompanha enquanto você digita. Comece por um exemplo ou escreva o seu.',
  'app.getStarted': 'Começar',
  'app.privacyNote': 'Tudo fica neste dispositivo. Sem conta, sem servidor, sem envio de arquivos.',
  'app.loading': 'Carregando o {product}…',
  'app.offlineReady': 'O {product} está pronto para funcionar sem internet.',
  'app.updateAvailable': 'Há uma nova versão do {product} disponível.',
  'app.reload': 'Recarregar',
  'app.errorTitle': 'Algo deu errado',
  'app.errorBody':
    'O {product} encontrou um erro inesperado. Seu trabalho fica guardado neste dispositivo, então recarregar costuma ser seguro.',
  'app.errorDetails': 'Detalhes técnicos',
  'app.errorReset': 'Recarregar o aplicativo',
  'app.version': 'Versão {version}',

  /* ---------------------------------------------------------------- *
   * Toolbar
   * ---------------------------------------------------------------- */
  'toolbar.new': 'Novo',
  'toolbar.open': 'Abrir',
  'toolbar.import': 'Importar',
  'toolbar.save': 'Salvar',
  'toolbar.export': 'Exportar',
  'toolbar.undo': 'Desfazer',
  'toolbar.redo': 'Refazer',
  'toolbar.format': 'Formatar',
  'toolbar.relayout': 'Reorganizar',
  'toolbar.theme': 'Tema',
  'toolbar.settings': 'Configurações',
  'toolbar.help': 'Ajuda',
  'toolbar.projects': 'Projetos',
  'toolbar.examples': 'Exemplos',
  'toolbar.share': 'Compartilhar',
  'toolbar.menu': 'Menu',
  'toolbar.more': 'Mais ações',
  'toolbar.label': 'Ações principais',

  /* ---------------------------------------------------------------- *
   * Panels
   * ---------------------------------------------------------------- */
  'panel.code': 'Código',
  'panel.diagram': 'Diagrama',
  'panel.split': 'Dividido',
  'panel.swapPanels': 'Inverter os painéis',
  'panel.codeOnly': 'Só o código',
  'panel.diagramOnly': 'Só o diagrama',
  'panel.problems': 'Problemas',
  'panel.outline': 'Estrutura',
  'panel.inspector': 'Inspetor',
  'panel.collapse': 'Recolher o painel',
  'panel.expand': 'Expandir o painel',
  'panel.resizeHandle': 'Redimensionar os painéis',
  'panel.inspectorEmpty': 'Selecione um nó para inspecioná-lo.',

  /* ---------------------------------------------------------------- *
   * Editor
   * ---------------------------------------------------------------- */
  'editor.placeholder': 'start;\nFaça algo;\nif (Pronto?) {{ Publique; }}\nend;',
  'editor.lineCol': 'Linha {line}, coluna {column}',
  'editor.autoUpdate': 'Atualizar enquanto eu digito',
  'editor.manualUpdate': 'Atualizar manualmente',
  'editor.updateNow': 'Atualizar agora',
  'editor.formatDocument': 'Formatar o documento',
  'editor.pendingChanges': 'O diagrama está esperando as suas alterações.',
  'editor.characters': '{count} caracteres',
  'editor.lines': '{count} linhas',
  'editor.label': 'Editor de pseudocódigo',

  /* ---------------------------------------------------------------- *
   * Diagram
   * ---------------------------------------------------------------- */
  'diagram.zoomIn': 'Aproximar',
  'diagram.zoomOut': 'Afastar',
  'diagram.fitToScreen': 'Ajustar à tela',
  'diagram.resetZoom': 'Restaurar o zoom',
  'diagram.zoomLevel': '{percent}%',
  'diagram.minimap': 'Minimapa',
  'diagram.grid': 'Grade',
  'diagram.direction': 'Direção do fluxo',
  'diagram.directionTopToBottom': 'De cima para baixo',
  'diagram.directionLeftToRight': 'Da esquerda para a direita',
  'diagram.expandFunctions': 'Expandir as funções',
  'diagram.collapseFunctions': 'Recolher as funções',
  'diagram.emptyTitle': 'Ainda não há nada para desenhar',
  'diagram.emptyBody': 'Escreva um passo à esquerda e ele aparece aqui.',
  'diagram.staleWarning':
    'Mostrando o último diagrama que compilou. Corrija os erros para atualizá-lo.',
  'diagram.truncatedWarning':
    'O diagrama foi interrompido em {count} nós. Recolha funções ou divida o fluxo para ver o restante.',
  'diagram.nodeCount': '{count} nós',
  'diagram.edgeCount': '{count} conexões',
  'diagram.label': 'Fluxograma',

  /* ---------------------------------------------------------------- *
   * Problems
   * ---------------------------------------------------------------- */
  'problems.title': 'Problemas',
  'problems.none': 'Nenhum problema encontrado.',
  'problems.errors': '{count} erros',
  'problems.warnings': '{count} avisos',
  'problems.infos': '{count} observações',
  'problems.summary': '{errors} erros, {warnings} avisos',
  'problems.goToLine': 'Ir para a linha {line}',
  'problems.filterAll': 'Todos',
  'problems.filterErrors': 'Erros',
  'problems.filterWarnings': 'Avisos',
  'problems.filterInfos': 'Observações',
  'problems.location': 'Linha {line}, coluna {column}',

  /* ---------------------------------------------------------------- *
   * Settings
   * ---------------------------------------------------------------- */
  'settings.title': 'Configurações',
  'settings.appearance': 'Aparência',
  'settings.theme': 'Tema',
  'settings.themeSystem': 'Igual ao sistema',
  'settings.themeLight': 'Claro',
  'settings.themeDark': 'Escuro',
  'settings.language': 'Idioma',
  'settings.languageSystem': 'Igual ao sistema',
  'settings.languageEn': 'English',
  'settings.languagePt': 'Português (Brasil)',
  'settings.languageNote':
    'A interface é traduzida; as palavras-chave do pseudocódigo continuam em inglês.',
  'settings.editor': 'Editor',
  'settings.editorFont': 'Fonte do editor',
  'settings.fontSize': 'Tamanho da fonte',
  'settings.fontSizeValue': '{size} px',
  'settings.debounce': 'Atraso da atualização',
  'settings.debounceValue': '{ms} ms',
  'settings.debounceHint':
    'Quanto tempo esperar, depois que você para de digitar, para redesenhar.',
  'settings.layout': 'Disposição',
  'settings.spacing': 'Espaçamento dos nós',
  'settings.spacingHint': 'Espaço entre as linhas e as colunas do diagrama.',
  'settings.direction': 'Direção do fluxo',
  'settings.branchLabels': 'Rótulos dos ramos',
  'settings.yesLabel': 'Rótulo do ramo verdadeiro',
  'settings.noLabel': 'Rótulo do ramo falso',
  'settings.showGrid': 'Mostrar a grade',
  'settings.reduceMotion': 'Reduzir as animações',
  'settings.reduceMotionHint': 'Desliga as animações e as transições do diagrama.',
  'settings.nodeColors': 'Colorir os nós por tipo',
  'settings.reset': 'Restaurar os padrões',
  'settings.resetConfirm': 'Restaurar todas as configurações para os valores padrão?',

  /* ---------------------------------------------------------------- *
   * Projects
   * ---------------------------------------------------------------- */
  'projects.title': 'Projetos',
  'projects.create': 'Novo projeto',
  'projects.rename': 'Renomear',
  'projects.duplicate': 'Duplicar',
  'projects.delete': 'Excluir',
  'projects.deleteConfirm': 'Excluir "{name}"? Não é possível desfazer.',
  'projects.search': 'Buscar projetos',
  'projects.empty': 'Nenhum projeto ainda.',
  'projects.emptyHint': 'Crie um, ou carregue um exemplo para começar.',
  'projects.noMatches': 'Nenhum projeto corresponde a "{query}".',
  'projects.lastEdited': 'Editado em {date}',
  'projects.untitled': 'Sem título',
  'projects.open': 'Abrir',
  'projects.saving': 'Salvando…',
  'projects.saved': 'Salvo',
  'projects.saveError': 'Não foi possível salvar. Suas últimas alterações estão apenas nesta aba.',
  'projects.quotaExceeded':
    'O armazenamento está cheio. Exclua um projeto ou exporte-o para um arquivo para liberar espaço.',
  'projects.nameLabel': 'Nome do projeto',
  'projects.copySuffix': '{name} (cópia)',
  'projects.count': '{count} projetos',

  /* ---------------------------------------------------------------- *
   * Examples
   * ---------------------------------------------------------------- */
  'examples.title': 'Exemplos',
  'examples.subtitle': 'Fluxos prontos para ler, executar e desmontar.',
  'examples.load': 'Carregar',
  'examples.loadConfirmTitle': 'Substituir o documento atual?',
  'examples.loadConfirmBody':
    'Carregar "{name}" substitui o que está no editor. As alterações não salvas serão perdidas.',
  'examples.cancel': 'Manter o meu trabalho',
  'examples.empty': 'Nenhum exemplo nesta categoria.',
  'examples.category.all': 'Todos',
  'examples.category.basics': 'Básicos',
  'examples.category.controlFlow': 'Controle de fluxo',
  'examples.category.functions': 'Funções',
  'examples.category.patterns': 'Padrões',
  'examples.category.algorithms': 'Algoritmos',

  /* ---------------------------------------------------------------- *
   * Export
   * ---------------------------------------------------------------- */
  'export.title': 'Exportar',
  'export.format': 'Formato',
  'export.code': 'Pseudocódigo',
  'export.json': 'Documento JSON',
  'export.svg': 'Imagem SVG',
  'export.png': 'Imagem PNG',
  'export.fileName': 'Nome do arquivo',
  'export.scale': 'Escala',
  'export.scaleValue': '{scale}×',
  'export.margin': 'Margem',
  'export.marginValue': '{margin} px',
  'export.transparentBackground': 'Fundo transparente',
  'export.includeBackground': 'Incluir o fundo',
  'export.visibleAreaOnly': 'Só a área visível',
  'export.wholeDiagram': 'O diagrama inteiro',
  'export.download': 'Baixar',
  'export.copyLink': 'Copiar o link',
  'export.linkCopied': 'Link copiado para a área de transferência.',
  'export.linkTooLong':
    'Este documento é longo demais para caber em um link. Exporte um arquivo e compartilhe-o.',
  'export.done': '{fileName} exportado.',
  'export.failed': 'A exportação falhou. Tente uma escala menor ou outro formato.',

  /* ---------------------------------------------------------------- *
   * Import
   * ---------------------------------------------------------------- */
  'import.title': 'Importar',
  'import.dropHere': 'Solte um arquivo aqui',
  'import.chooseFile': 'Escolher um arquivo',
  'import.accepted': 'Aceita {extensions}',
  'import.reading': 'Lendo {name}…',
  'import.invalidFile': 'Esse arquivo não é um documento do {product}.',
  'import.unsupportedVersion':
    'Este arquivo foi gerado por uma versão mais nova ({version}). Atualize o {product} e tente de novo.',
  'import.importAsNew': 'Importar como um novo projeto',
  'import.replaceCurrent': 'Substituir o documento atual',
  'import.tooLarge': 'Esse arquivo tem {size}, acima do limite de {limit}.',
  'import.done': '{name} importado.',

  /* ---------------------------------------------------------------- *
   * Accessibility
   * ---------------------------------------------------------------- */
  'a11y.skipToEditor': 'Ir para o editor',
  'a11y.skipToDiagram': 'Ir para o diagrama',
  'a11y.skipToProblems': 'Ir para a lista de problemas',
  'a11y.landmarkBanner': 'Cabeçalho do aplicativo',
  'a11y.landmarkToolbar': 'Barra de ferramentas',
  'a11y.landmarkMain': 'Editor e diagrama',
  'a11y.landmarkEditor': 'Editor de pseudocódigo',
  'a11y.landmarkDiagram': 'Fluxograma',
  'a11y.landmarkProblems': 'Problemas',
  'a11y.landmarkOutline': 'Estrutura do diagrama',
  'a11y.landmarkInspector': 'Inspetor de nós',
  'a11y.landmarkStatus': 'Barra de status',
  'a11y.outlineTitle': 'Estrutura do diagrama',
  'a11y.outlineHint': 'A ordem de leitura do diagrama. Escolha um item para revelá-lo no código.',
  'a11y.outlineEmpty': 'O diagrama está vazio.',
  'a11y.outlineItem': '{role}: {label}',
  'a11y.outlineLevel': 'Nível {depth}',
  'a11y.outlineBranch': 'Ramo {label}',
  'a11y.outlineFunction': 'Função {name}',
  'a11y.outlineLeadsTo': 'Leva a {label}',
  'a11y.outlineLoopsBackTo': 'Volta para {label}',
  'a11y.diagramUpdated': 'Diagrama atualizado, {nodes} nós',
  'a11y.diagramEmpty': 'O diagrama está vazio',
  'a11y.diagramUnchanged': 'Diagrama sem alterações',
  'a11y.problemsAnnounce': '{errors} erros e {warnings} avisos',
  'a11y.problemsCleared': 'Todos os problemas foram resolvidos',
  'a11y.nodeSelected': '{label} selecionado',
  'a11y.nodeDeselected': 'Seleção limpa',
  'a11y.zoomAnnounce': 'Zoom em {percent} por cento',
  'a11y.savedAnnounce': 'Projeto salvo',
  'a11y.themeChanged': 'Tema alterado para {theme}',
  'a11y.languageChanged': 'Idioma alterado para {language}',
  'a11y.role.start': 'Início',
  'a11y.role.end': 'Fim',
  'a11y.role.action': 'Passo',
  'a11y.role.decision': 'Decisão',
  'a11y.role.loop': 'Laço',
  'a11y.role.call': 'Chamada de função',
  'a11y.role.io': 'Entrada ou saída',
  'a11y.role.junction': 'Junção',
  'a11y.role.note': 'Nota',
  'a11y.role.return': 'Retorno',
  'a11y.role.function-entry': 'Início da função',
  'a11y.role.function-exit': 'Fim da função',
  'a11y.role.error': 'Erro',

  /* ---------------------------------------------------------------- *
   * Keyboard shortcuts
   * ---------------------------------------------------------------- */
  'shortcuts.title': 'Atalhos de teclado',
  'shortcuts.groupGeneral': 'Geral',
  'shortcuts.groupEditor': 'Editor',
  'shortcuts.groupDiagram': 'Diagrama',
  'shortcuts.groupNavigation': 'Navegação',
  'shortcuts.newProject': 'Novo projeto',
  'shortcuts.openProjects': 'Abrir a lista de projetos',
  'shortcuts.save': 'Salvar o projeto',
  'shortcuts.import': 'Importar um arquivo',
  'shortcuts.export': 'Abrir a janela de exportação',
  'shortcuts.undo': 'Desfazer',
  'shortcuts.redo': 'Refazer',
  'shortcuts.formatDocument': 'Formatar o documento',
  'shortcuts.updateNow': 'Atualizar o diagrama agora',
  'shortcuts.find': 'Buscar no código',
  'shortcuts.toggleComment': 'Comentar ou descomentar a linha',
  'shortcuts.focusEditor': 'Levar o foco para o editor',
  'shortcuts.focusDiagram': 'Levar o foco para o diagrama',
  'shortcuts.focusProblems': 'Levar o foco para a lista de problemas',
  'shortcuts.nextProblem': 'Ir para o próximo problema',
  'shortcuts.previousProblem': 'Ir para o problema anterior',
  'shortcuts.zoomIn': 'Aproximar',
  'shortcuts.zoomOut': 'Afastar',
  'shortcuts.fitToScreen': 'Ajustar o diagrama à tela',
  'shortcuts.resetZoom': 'Restaurar o zoom',
  'shortcuts.toggleMinimap': 'Mostrar ou ocultar o minimapa',
  'shortcuts.toggleGrid': 'Mostrar ou ocultar a grade',
  'shortcuts.swapPanels': 'Inverter os painéis',
  'shortcuts.togglePanelLayout': 'Alternar a disposição dos painéis',
  'shortcuts.toggleTheme': 'Alternar entre claro e escuro',
  'shortcuts.openSettings': 'Abrir as configurações',
  'shortcuts.openHelp': 'Abrir a lista de atalhos',
  'shortcuts.closeDialog': 'Fechar a janela',

  /* ---------------------------------------------------------------- *
   * Shared vocabulary
   * ---------------------------------------------------------------- */
  'common.ok': 'OK',
  'common.cancel': 'Cancelar',
  'common.close': 'Fechar',
  'common.confirm': 'Confirmar',
  'common.delete': 'Excluir',
  'common.rename': 'Renomear',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.retry': 'Tentar de novo',
  'common.dismiss': 'Dispensar',
  'common.yes': 'Sim',
  'common.no': 'Não',
  'common.loading': 'Carregando…',
  'common.error': 'Erro',
  'common.save': 'Salvar',
  'common.apply': 'Aplicar',
  'common.done': 'Concluído',
  'common.back': 'Voltar',
  'common.search': 'Buscar',
  'common.none': 'Nenhum',

  /* ---------------------------------------------------------------- *
   * Diagnostics
   * ---------------------------------------------------------------- */
  'diagnostics.LEX001': 'Este texto não é fechado antes do fim da linha.',
  'diagnostics.LEX001.hint': 'Adicione a aspa de fechamento que falta.',
  'diagnostics.LEX002': 'O comentário aberto na linha {line} nunca é fechado.',
  'diagnostics.LEX002.hint': 'Feche-o com {close}.',
  'diagnostics.LEX003': 'Caractere inesperado "{character}".',
  'diagnostics.LEX003.hint': 'Remova-o, ou coloque-o dentro de um rótulo.',

  'diagnostics.SYN001': 'O bloco aberto na linha {line} nunca é fechado.',
  'diagnostics.SYN001.hint': 'Adicione a chave que fecha o bloco aberto na linha {line}.',
  'diagnostics.SYN002': 'Esta chave de fechamento não tem uma chave de abertura correspondente.',
  'diagnostics.SYN002.hint': 'Remova-a, ou abra um bloco antes dela.',
  'diagnostics.SYN003': 'O parêntese aberto na linha {line} nunca é fechado.',
  'diagnostics.SYN003.hint': 'Adicione o parêntese de fechamento.',
  'diagnostics.SYN004': 'Esta instrução não termina com ponto e vírgula.',
  'diagnostics.SYN004.hint': 'Adicione um ponto e vírgula no fim da linha {line}.',
  'diagnostics.SYN005': '"else" não tem um "if" correspondente.',
  'diagnostics.SYN005.hint': 'Adicione um bloco "if" antes dele, ou apague o "else".',
  'diagnostics.SYN006': '"case" só pode aparecer dentro de um bloco "switch".',
  'diagnostics.SYN006.hint': 'Mova este case para dentro de um bloco "switch".',
  'diagnostics.SYN007': '"{keyword}" precisa de uma condição entre parênteses.',
  'diagnostics.SYN007.hint': 'Escreva a condição entre parênteses, por exemplo (Pronto?).',
  'diagnostics.SYN008': '"{keyword}" precisa de um bloco entre chaves.',
  'diagnostics.SYN008.hint': 'Coloque entre chaves os passos que pertencem a ele.',
  'diagnostics.SYN009': 'Esta função não tem nome.',
  'diagnostics.SYN009.hint': 'Escreva "function", o nome e, em seguida, o corpo entre chaves.',
  'diagnostics.SYN010': '"call" precisa do nome de uma função.',
  'diagnostics.SYN010.hint': 'Escreva "call" seguido do nome da função.',
  'diagnostics.SYN011': 'Um bloco "repeat" precisa terminar com "until".',
  'diagnostics.SYN011.hint': 'Adicione "until" e uma condição depois da chave de fechamento.',
  'diagnostics.SYN012': '"{found}" inesperado; era esperado {expected}.',
  'diagnostics.SYN012.hint': 'Confira a sintaxe perto da linha {line}.',
  'diagnostics.SYN013': 'Este rótulo de case não termina com dois-pontos.',
  'diagnostics.SYN013.hint': 'Adicione dois-pontos depois do valor do case.',
  'diagnostics.SYN014': 'Este rótulo está vazio.',
  'diagnostics.SYN014.hint': 'Descreva o passo, ou remova o rótulo.',
  'diagnostics.SYN015': '"for each" precisa de um cabeçalho como (item in collection).',
  'diagnostics.SYN015.hint': 'Diga o que você percorre, por exemplo (Pedido in pedidos).',
  'diagnostics.SYN016': '"default" só pode aparecer dentro de um bloco "switch".',
  'diagnostics.SYN016.hint': 'Mova esta cláusula default para dentro de um bloco "switch".',

  'diagnostics.SEM001': 'A função "{name}" já foi declarada na linha {line}.',
  'diagnostics.SEM001.hint': 'Renomeie esta, ou remova a declaração anterior.',
  'diagnostics.SEM002': 'Nenhuma função chamada "{name}" foi declarada.',
  'diagnostics.SEM002.hint': 'Declare-a, ou confira a grafia.',
  'diagnostics.SEM003': '"break" só faz sentido dentro de um laço.',
  'diagnostics.SEM003.hint': 'Mova-o para dentro de um laço, ou remova-o.',
  'diagnostics.SEM004': '"continue" só faz sentido dentro de um laço.',
  'diagnostics.SEM004.hint': 'Mova-o para dentro de um laço, ou remova-o.',
  'diagnostics.SEM005': '"return" só faz sentido dentro de uma função.',
  'diagnostics.SEM005.hint': 'Use "end" para encerrar o fluxo principal.',
  'diagnostics.SEM006': 'O fluxo sempre para antes deste passo, então ele nunca é executado.',
  'diagnostics.SEM006.hint': 'Mova-o para antes da linha {line}, ou remova-o.',
  'diagnostics.SEM007': 'Este bloco está vazio.',
  'diagnostics.SEM007.hint': 'Adicione um passo, ou remova o bloco.',
  'diagnostics.SEM008': 'Este rótulo de ramo está vazio.',
  'diagnostics.SEM008.hint': 'Escreva um rótulo, ou deixe-o de fora para usar o padrão.',
  'diagnostics.SEM009': 'A função "{name}" chama a si mesma.',
  'diagnostics.SEM009.hint':
    'Chamadas recursivas são desenhadas recolhidas para manter o diagrama finito.',
  'diagnostics.SEM010': 'A função "{name}" faz parte de um ciclo de chamadas: {cycle}.',
  'diagnostics.SEM010.hint': 'Quebre o ciclo, ou deixe essas chamadas recolhidas.',
  'diagnostics.SEM011': 'O id "{id}" já é usado na linha {line}.',
  'diagnostics.SEM011.hint': 'Dê a este nó um id próprio.',
  'diagnostics.SEM012': 'A função "{name}" nunca é chamada.',
  'diagnostics.SEM012.hint': 'Chame-a a partir do fluxo, ou remova a declaração.',
  'diagnostics.SEM013': 'Anotação desconhecida "{name}".',
  'diagnostics.SEM013.hint': 'Anotações aceitas: {supported}.',
  'diagnostics.SEM014': '"{value}" não é um valor válido para a anotação "{name}".',
  'diagnostics.SEM014.hint': 'Era esperado {expected}.',
  'diagnostics.SEM015': 'Este switch já tem uma cláusula default na linha {line}.',
  'diagnostics.SEM015.hint': 'Mantenha uma única cláusula default e remova as demais.',
  'diagnostics.SEM016': 'Este switch não tem nenhum case.',
  'diagnostics.SEM016.hint': 'Adicione um case, ou troque o switch por um passo simples.',
  'diagnostics.SEM017': 'Passos escritos depois de "end" nunca são alcançados.',
  'diagnostics.SEM017.hint': 'Mova-os para antes do "end" da linha {line}.',
  'diagnostics.SEM018': 'O fluxo tem {count} pontos de início.',
  'diagnostics.SEM018.hint': 'Mantenha um só início; o próximo está na linha {line}.',
  'diagnostics.SEM019': 'O fluxo não tem um início explícito.',
  'diagnostics.SEM019.hint': 'Adicione "start" como primeiro passo.',

  'diagnostics.LIM001': 'O documento tem {size} caracteres, acima do limite de {limit}.',
  'diagnostics.LIM001.hint': 'Divida-o em funções, ou em projetos separados.',
  'diagnostics.LIM002': 'Os blocos estão aninhados em {depth} níveis, acima do limite de {limit}.',
  'diagnostics.LIM002.hint': 'Mova os blocos internos para funções.',
  'diagnostics.LIM003': 'O diagrama teria {count} nós, acima do limite de {limit}.',
  'diagnostics.LIM003.hint': 'Recolha funções, ou divida o fluxo em projetos.',
  'diagnostics.LIM004': 'A expansão de funções parou na profundidade {depth}.',
  'diagnostics.LIM004.hint': 'Recolha algumas funções para ver o fluxo inteiro.',
};
